import React, { useState, useEffect } from "react";
import allApi from "../../../api/allApi";
import UserAddModal from "../common/UserAddModal";
import EditUserModal from "../common/EditUserModal";
import errorIcon from "../../../assets/images/error-2.png";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import successIcon from "../../../assets/images/success_lg.png";
import RoleInfo from "../common/RoleInfo";
import Loader from "../common/Loader";
import { useLocalization } from "../localization/LocalizationContext";
import { useNetwork } from "../common/networkContext";
import NmsTableSearch, { searchRows, sortRows } from "../common/NmsTableSearch";

function UserManagement() {
  const { localizationStrings } = useLocalization();
  const { selectedNetworkId } = useNetwork();
  const networkId = selectedNetworkId?.id ?? 6001;

  const [roleName, setRoleName] = useState("");
  const [permissionName, setPermissionName] = useState("");
  const [selectedRole, setSelectedRole] = useState("ROLE_CUSTOMER_ADMIN");
  const [roles, setRoles] = useState([]);
  const [showUserPopUp, setShowUserPopUp] = useState(false);
  const [users, setUsers] = useState([]);
  const [tableQuery, setTableQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState([]);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState("user");
  const [permissions, setPermissions] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [permissionDescription, setPermissionDescription] = useState("");
  const [allPermission, setAllPermission] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [allFeatures, setAllFeatures] = useState("");
  const [deviceIdToDelete, setDeviceIdToDelete] = useState(null);
  const [modalContent, setModalContent] = useState({
    title: "",
    message: "User has been successfully updated.",
  });
  const [aboveSelected, setAboveSelected] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showRoleInfo, setShowRoleInfo] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [individualCheckboxChecked, setIndividualCheckboxChecked] = useState(
    {}
  );
  const [selectAllbox, setSelectAllbox] = useState(false);
  const [individualboxChecked, setIndividualboxChecked] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [selectedFeatures, setSelectedFeatures] = useState([]);

  useEffect(() => {
    fetchPermissions();
    fetchPermissionsForRoles();
    handleFeatureWisePermission();
    fetchPermissionsWithRoles();
  }, []);

  useEffect(()=>{
   fetchUsers(networkId)
  },[networkId]);

  useEffect(() => {
    if (selectedRole) {
      const role = roles.find((r) => r.name === selectedRole);

      if (role && Array.isArray(role.features)) {
        const rolePermissions = role.features.flatMap((feature) =>
          feature.permissions.map((permission) => ({ id: permission.id }))
        );
        setSelectedPermissions(rolePermissions || []);
      } else {
        setSelectedPermissions([]); // Reset if role or features are undefined
      }
    } else {
      setSelectedPermissions([]);
    }
  }, [selectedRole]);

  useEffect(() => {
    if (selectedRole && selectedPermissions.length > 0) {
      setIsSaveDisabled(false);
    } else {
      setIsSaveDisabled(true);
    }
  }, [selectedRole, selectedPermissions]);

  const fetchUsers = async () => {
    setLoading(true);
    setRefreshing(true);
    try {
      const api = new allApi();
      let response = await api.getRequest(
        `api/user/v2/findAllUsers/${networkId}`
      );

      if (response && Array.isArray(response)) {
        response = response.reverse();
        setUsers(response);
      } else {
        setUsers([]);
      }

      // ✅ RESET CHECKBOXES AFTER DATA REFRESH
      setSelectAllChecked(false);
      setIndividualCheckboxChecked({});

      await fetchRoles();
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchRoles = async () => {
    setLoading(true);
    setRefreshing(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(`api/user/findAllRoles`);
      setRoles(response);
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const handleEditUser = async (userId) => {
    try {
      const api = new allApi();
      const response = await api.getRequest(`api/user/${userId}`);
      if (response) {
        setSelectedUser(response);
        setShowEditModal(true);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest("api/user/findAllPermissions");
      setPermissions(response);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  const handleDeleteUser = async (userToDelete) => {
    try {
      const api = new allApi();

      const payload = {
        emails: userToDelete.map((user) => user.email),
      };

      const response = await api.deleteRequest("api/user/delete", payload);

      if (response) {
        // Remove users from UI
        setUsers((prevListings) =>
          prevListings.filter(
            (user) =>
              !userToDelete.some(
                (toDelete) => toDelete.email === user.user.email
              )
          )
        );

        // ✅ Show success popup with backend message
        setModalContent({
          title: "Success",
          message:
            response.message ||
            `${payload.emails.length} user(s) deleted successfully.`,
        });
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Error deleting user:", error);

      // ❌ Show failure popup dynamically
      setErrorMessage(
        error?.response?.data?.message ||
          "Failed to delete user. Please try again."
      );
      setShowErrorModal(true);
    }
  };

  const handleAddRole = async () => {
    if (!roleName) {
      setErrorMessage("Role Name cannot be empty");
      setShowErrorModal(true);
      return;
    }
    try {
      const api = new allApi();
      const payload = {
        name: roleName,
        above: aboveSelected,
        below: !aboveSelected,
        role_name: selectedRole,
      };
      const response = await api.postRequest(`api/user/add_role`, "", payload);
      const newRole = response.data.role;
      setRoleName("");
      fetchUsers(networkId);
      fetchRoles();
      const modal = document.getElementById("addRole");
      if (modal) {
        const bootstrapModal = new Modal(modal);
        bootstrapModal.hide();
      }
      setShowSuccessModal(true);
      await fetchPermissionsForRoles();
    } catch (error) {
      console.error("Error adding role:", error);
      showSuccessPopUp({ message: "Role has been added" });
    }
  };

  const handleAddPermission = async () => {
    if (!permissionName) {
      setErrorMessage("Permission Name cannot be empty");
      setShowErrorModal(true);
      return;
    }
    try {
      const api = new allApi();
      const response = await api.postRequest("api/user/add_permission", "", {
        name: permissionName,
        description: permissionDescription,
      });
      setPermissionName("");
      fetchPermissions();
    } catch (error) {
      console.error("Error adding permission:", error);
    }
  };

  const handleAssignPermission = async () => {
    if (!selectedRole || selectedPermissions.length === 0) {
      setErrorMessage("Role and at least one permission must be selected");
      setShowErrorModal(true);
      return;
    }
    try {
      const api = new allApi();
      const selectedPermissionNames = selectedPermissions.map(
        (permission) => permission.name
      );

      const payload = {
        role: selectedRole,
        permission: selectedPermissionNames,
      };

      const response = await api.postRequest(
        `api/user/assign_permission_to_role`,
        "",
        payload
      );
      setSelectedRole("");
      setSelectedPermissions([]);
      fetchPermissionsForRoles();
      handleFeatureWisePermission();
      setModalContent({
        title: "Success",
        message: "Permissions successfully assigned to the role.",
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error assigning permission to role:", error);
    }
  };

  const deletePermissionFromRole = async () => {
    try {
      const api = new allApi();
      const payload = {
        role: "ROLE_14",
        permission: "DELETE_AUDIT",
      };
      const response = await api.deleteRequest(
        "api/user/delete_permission_from_role",
        "",
        payload
      );
      if (response) {
        console.log("permission deleted successfully", response);
        fetchPermissionsForRoles();
        fetchRoles();
      }
    } catch (error) {
      console.error("Error deleting permission", error);
    }
  };

  const getPermissionsForRole = (roleName) => {
    const role = roles.find((r) => r.name === roleName);
    if (role && role.permissions) {
      return role.permissions.split(", ").map((permission) => ({
        id: permission, // Use permission string as ID
        description: permission, // Use permission string as description
      }));
    }
    return [];
  };

  const fetchPermissionsForRoles = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest("api/user/permissionsForRoles");

      if (response && Array.isArray(response)) {
        const featuresByRole = response.map((role) => ({
          role_id: role.role_id,
          role_name: role.role_name,
          features: role.features.map((feature) => ({
            feature_id: feature.feature_id,
            feature_name: feature.feature_name,
            permissions: Object.entries(feature.permission).map(
              ([permName, value]) => ({
                permission_name: permName,
                isGranted: value === 1, // Store as boolean
              })
            ),
          })),
        }));

        setAllPermission(featuresByRole);

        // Auto-select "ROLE_CUSTOMER_ADMIN"
        const defaultRole = featuresByRole.find(
          (role) => role.role_name === "ROLE_CUSTOMER_ADMIN"
        );
        if (defaultRole) {
          setSelectedFeatures(defaultRole.features);
        }
      } else {
        console.error("Invalid response format:", response);
      }
    } catch (error) {
      console.error("Error fetching permissions for roles:", error);
    }
  };

  const fetchPermissionsWithRoles = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest("api/user/permissionsForRoles");

      if (response && Array.isArray(response)) {
        const featuresByRole = response.map((role) => ({
          role_id: role.role_id,
          role_name: role.role_name,
          features: role.features.map((feature) => ({
            feature_id: feature.feature_id,
            feature_name: feature.feature_name,
            permissions: Object.entries(feature.permission)
              .filter(([_, value]) => value === 1)
              .map(([permName]) => permName),
          })),
        }));

        setAllPermissions(featuresByRole);
      } else {
        console.error("Invalid response format:", response);
      }
    } catch (error) {
      console.error("Error fetching permissions for roles:", error);
    }
  };

  const handleRoleChange = (roleName) => {
    setSelectedRole(roleName);
    const roleData = allPermission.find((role) => role.role_name === roleName);

    if (roleData) {
      setSelectedFeatures(roleData.features);
    } else {
      setSelectedFeatures([]);
    }
  };

  const handleFeatureWisePermission = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest("api/user/featurewisePermissions");
      if (Array.isArray(response)) {
        setAllFeatures(response);
      } else {
        console.error("Unexpected response format:", response);
        setAllFeatures([]);
      }
    } catch (error) {
      console.error("Error fetching permissions for roles:", error);
      setAllFeatures([]);
    }
  };

  const handleDeleteAssignPermissionRole = async (roleId) => {
    try {
      const api = new allApi();
      const response = await api.deleteRequest(
        `api/user/delete_role/${roleId}`
      );

      if (response.status === 200) {
        fetchRoles();
        setSelectedRole();
      } else {
        console.error("Failed to delete role:", response.statusText);
        setShowErrorModal(true);
      }
    } catch (error) {
      setShowErrorModal(true);
      console.error("Error deleting permission from role:", error.message);
    }
  };

  const handlePermissionCheckboxChange = (featureId, permissionName) => {
    setSelectedFeatures((prevFeatures) =>
      prevFeatures.map((feature) =>
        feature.feature_id === featureId
          ? {
              ...feature,
              permissions: feature.permissions.map((perm) =>
                perm.permission_name === permissionName
                  ? { ...perm, isGranted: !perm.isGranted }
                  : perm
              ),
            }
          : feature
      )
    );
  };

  const handleAddButtonClick = () => {
    setShowUserPopUp(true);
  };

  const handleUserClose = () => {
    setShowUserPopUp(false);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
  };

  const handleDeleteButtonClick = (email) => {
    const user = users.find((u) => u.user.email === email);
    if (user) {
      setUserToDelete([user.user]);
    }
  };

  const handleDeleteConfirmation = async () => {
    if (!userToDelete || userToDelete.length === 0) return;

    await handleDeleteUser(userToDelete);
    setUserToDelete([]);
  };

  const handleDeleteRoleConfirmation = () => {
    if (roleToDelete) {
      handleDeleteAssignPermissionRole(roleToDelete);
      setUserToDelete(null);
    }
  };
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
  };

  const handleSelectAllCheckbox = () => {
    const newValue = !selectAllChecked;
    setSelectAllChecked(newValue);
    const newCheckboxState = {};
    users.forEach((userData) => {
      newCheckboxState[userData.user.userId] = newValue;
    });
    setIndividualCheckboxChecked(newCheckboxState);
  };

  const handleIndividualCheckboxChange = (userId) => {
    setIndividualCheckboxChecked((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const handleSelectAllbox = () => {
    const newboxState = {};
    allPermission.forEach((role) => {
      newboxState[role.role_name] = !selectAllbox;
    });
    setIndividualboxChecked(newboxState);
    setSelectAllbox((prev) => !prev);
  };

  const handleIndividualboxChange = (role_id) => {
    setIndividualboxChecked((prev) => ({
      ...prev,
      [role_id]: !prev[role_id],
    }));
  };

  const toggleTab = (index) => {
    setActiveTab(index);
  };

  const showSuccessPopUp = (response) => {
    setModalContent({ title: "Success", message: response.message });
    setShowSuccessModal(true);
  };
  const handleSuccessAddRole = (message) => {
    setModalContent({ title: "Success", message });
    setShowSuccessModal(true);
  };

  const handleMoreInfoClick = (e) => {
    e.preventDefault();
    setShowRoleInfo(true);
  };
  const resetForm = () => {
    setRoleName("");
    setSelectedRole("");
  };

  const handleCloseRoleInfo = () => {
    resetForm();
    setShowRoleInfo(false);
  };
  const handleCloseMoreInfo = () => {
    setShowRoleInfo(true);
  };
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const filteredUsers = sortRows(
    searchRows(users, tableQuery, [
      "user.username",
      "user.firstname",
      "user.lastname",
      "user.email",
      "user.role.name",
    ]),
    sortConfig.key,
    sortConfig.direction
  );
  const totalFilteredUsers = filteredUsers.length;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(totalFilteredUsers / usersPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleLastPage = () => {
    setCurrentPage(totalPages);
  };
  const handleFirstPage = () => {
    setCurrentPage(1);
  };
  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const range = 1;
    const start = Math.max(1, currentPage - range);
    const end = Math.min(totalPages, currentPage + range);

    return [...Array(end - start + 1).keys()].map((i) => start + i);
  };

  const prepareBulkDelete = () => {
    const selectedUsers = users
      .filter((u) => individualCheckboxChecked[u.user.userId])
      .map((u) => u.user);

    if (selectedUsers.length > 0) {
      setUserToDelete(selectedUsers);
    }
  };

  const selectedUserIds = Object.keys(individualCheckboxChecked).filter(
    (id) => individualCheckboxChecked[id]
  );

  const selectedCount = selectedUserIds.length;

  return (
    <div className="nms-dash">
      <div className="inc-card ch-100">
        <div className="inc-card-title">
          <ul className="nav nav-underline" id="myTab" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className="nav-link um active"
                id="user-tab"
                data-bs-toggle="tab"
                data-bs-target="#user-tab-pane"
                type="button"
                role="tab"
                aria-controls="user-tab-pane"
                aria-selected="true"
              >
                {localizationStrings?.usersAccount}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className="nav-link rm"
                id="role-tab"
                data-bs-toggle="tab"
                data-bs-target="#role-tab-pane"
                type="button"
                role="tab"
                aria-controls="role-tab-pane"
                aria-selected="false"
              >
                {localizationStrings?.roles}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className="nav-link pm"
                id="permission-tab"
                data-bs-toggle="tab"
                data-bs-target="#permission-tab-pane"
                type="button"
                role="tab"
                aria-controls="permission-tab-pane"
                aria-selected="false"
              >
                {localizationStrings?.permissions}
              </button>
            </li>
          </ul>
          <div className="inc-card-button user-button">
            <NmsTableSearch
              value={tableQuery}
              onChange={(value) => {
                setTableQuery(value);
                setCurrentPage(1);
              }}
              placeholder={localizationStrings?.search || "Search users"}
            />
            <button
              className="text-btn primary-btn"
              onClick={handleAddButtonClick}
            >
              {localizationStrings?.addUser}
            </button>
            <button
              className="icon-btn"
              data-bs-toggle={selectedCount > 1 ? "modal" : ""}
              data-bs-target={selectedCount > 1 ? "#noDeviceDelete" : ""}
              onClick={selectedCount > 1 ? prepareBulkDelete : undefined}
              disabled={selectedCount <= 1}
              title={
                selectedCount <= 1
                  ? "Select more than one user to enable bulk delete"
                  : "Delete selected users"
              }
            >
              <i className="fa fa-trash" aria-hidden="true"></i>
            </button>

            <button
              className="icon-btn"
              title="Refresh"
              onClick={() => fetchUsers(networkId)}
            >
              {refreshing ? (
                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
              ) : (
                <i className="fa fa-refresh" aria-hidden="true"></i>
              )}
            </button>
          </div>
          <div className="inc-card-button role-button">
            {/* <button
              className="text-btn primary-btn"
              data-bs-toggle="modal"
              data-bs-target="#addRole"
            >
              {localizationStrings?.addRole}
            </button>
            <button
              className="icon-btn"
              title="Delete"
              data-bs-toggle="modal"
              data-bs-target="#noDeviceDelete"
              onClick={() => {
                handleDeleteAssignPermissionRole();
                fetchPermissionsForRoles();
              }}
            >
              <i className="fa fa-trash" aria-hidden="true"></i>
            </button> */}
            <button
              className="icon-btn"
              title="Refresh"
              onClick={() => fetchRoles()}
            >
              {refreshing ? (
                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
              ) : (
                <i className="fa fa-refresh" aria-hidden="true"></i>
              )}
            </button>
          </div>
        </div>

        <div className="inc-card-body">
          <div className="tab-content" id="myTabContent">
            <div
              className="tab-pane fade show active"
              id="user-tab-pane"
              role="tabpanel"
              aria-labelledby="user-tab"
              tabIndex="0"
            >
              <div className="tab-pagination">
                {/* ====== USER TAB ====== */}
                <div className="inc-card-table">
                  <table className="full-table m-head">
                    <thead>
                      <tr>
                        <th>
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              checked={selectAllChecked}
                              onChange={() => handleSelectAllCheckbox()}
                            />
                            <span className="indicator"></span>
                          </label>
                        </th>
                        <th
                          className={`inc-th-sort${sortConfig.key === "user.username" ? " is-active" : ""}`}
                          onClick={() =>
                            setSortConfig((prev) => ({
                              key: "user.username",
                              direction:
                                prev.key === "user.username" &&
                                prev.direction === "asc"
                                  ? "desc"
                                  : "asc",
                            }))
                          }
                        >
                          {localizationStrings?.username}
                          <i className="fa fa-sort" aria-hidden="true" />
                        </th>
                        <th
                          className={`inc-th-sort${sortConfig.key === "user.firstname" ? " is-active" : ""}`}
                          onClick={() =>
                            setSortConfig((prev) => ({
                              key: "user.firstname",
                              direction:
                                prev.key === "user.firstname" &&
                                prev.direction === "asc"
                                  ? "desc"
                                  : "asc",
                            }))
                          }
                        >
                          {localizationStrings?.firstname}
                          <i className="fa fa-sort" aria-hidden="true" />
                        </th>
                        <th
                          className={`inc-th-sort${sortConfig.key === "user.lastname" ? " is-active" : ""}`}
                          onClick={() =>
                            setSortConfig((prev) => ({
                              key: "user.lastname",
                              direction:
                                prev.key === "user.lastname" &&
                                prev.direction === "asc"
                                  ? "desc"
                                  : "asc",
                            }))
                          }
                        >
                          {localizationStrings?.lastname}
                          <i className="fa fa-sort" aria-hidden="true" />
                        </th>
                        <th
                          className={`inc-th-sort${sortConfig.key === "user.email" ? " is-active" : ""}`}
                          onClick={() =>
                            setSortConfig((prev) => ({
                              key: "user.email",
                              direction:
                                prev.key === "user.email" &&
                                prev.direction === "asc"
                                  ? "desc"
                                  : "asc",
                            }))
                          }
                        >
                          {localizationStrings?.email}
                          <i className="fa fa-sort" aria-hidden="true" />
                        </th>
                        <th
                          className={`inc-th-sort${sortConfig.key === "user.role.name" ? " is-active" : ""}`}
                          onClick={() =>
                            setSortConfig((prev) => ({
                              key: "user.role.name",
                              direction:
                                prev.key === "user.role.name" &&
                                prev.direction === "asc"
                                  ? "desc"
                                  : "asc",
                            }))
                          }
                        >
                          {localizationStrings?.role}
                          <i className="fa fa-sort" aria-hidden="true" />
                        </th>
                        <th className="w200">{localizationStrings?.action}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7">
                            <Loader />
                          </td>
                        </tr>
                      ) : currentUsers.length ? (
                        currentUsers.map((userData) => (
                          <tr key={userData.user.email}>
                            <td>
                              <label className="checkbox">
                                <input
                                  type="checkbox"
                                  checked={
                                    individualCheckboxChecked[
                                      userData.user.userId
                                    ] || false
                                  }
                                  onChange={() =>
                                    handleIndividualCheckboxChange(
                                      userData.user.userId
                                    )
                                  }
                                />
                                <span className="indicator"></span>
                              </label>
                            </td>
                            <td>{userData.user.username || "N/A"}</td>
                            <td>{userData.user.firstname || "N/A"}</td>
                            <td>{userData.user.lastname || "N/A"}</td>
                            <td>{userData.user.email || "N/A"}</td>
                            <td>{userData.user.role.name || "N/A"}</td>
                            <td className="w200">
                              <div className="button-group">
                                <button
                                  title="Edit"
                                  onClick={() =>
                                    handleEditUser(userData.user.userId)
                                  }
                                >
                                  <i
                                    className="fa fa-pencil"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                                <button
                                  title={
                                    selectedCount === 1 &&
                                    individualCheckboxChecked[
                                      userData.user.userId
                                    ]
                                      ? "Delete this user"
                                      : "Select only this user to enable delete"
                                  }
                                  data-bs-toggle={
                                    selectedCount === 1 &&
                                    individualCheckboxChecked[
                                      userData.user.userId
                                    ]
                                      ? "modal"
                                      : ""
                                  }
                                  data-bs-target={
                                    selectedCount === 1 &&
                                    individualCheckboxChecked[
                                      userData.user.userId
                                    ]
                                      ? "#noDeviceDelete"
                                      : ""
                                  }
                                  onClick={() =>
                                    selectedCount === 1 &&
                                    individualCheckboxChecked[
                                      userData.user.userId
                                    ] &&
                                    handleDeleteButtonClick(userData.user.email)
                                  }
                                  disabled={
                                    !(
                                      selectedCount === 1 &&
                                      individualCheckboxChecked[
                                        userData.user.userId
                                      ]
                                    )
                                  }
                                >
                                  <i
                                    className="fa fa-trash"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7">
                            <div className="nms-empty">
                              {localizationStrings?.noData}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="pagination-container">
                  <div className="pagination-info">
                    <p>
                      Showing{" "}
                      {totalFilteredUsers === 0 ? 0 : indexOfFirstUser + 1}–
                      {Math.min(indexOfLastUser, totalFilteredUsers)} of{" "}
                      {totalFilteredUsers} Records
                    </p>
                  </div>

                  <div className="pagination">
                    <button
                      onClick={handleFirstPage}
                      disabled={currentPage === 1}
                    >
                      {localizationStrings?.firstPage}
                    </button>
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                    >
                      {localizationStrings?.previous}
                    </button>
                    {getPageNumbers().map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageClick(page)}
                        className={currentPage === page ? "active" : ""}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                    >
                      {localizationStrings?.next}
                    </button>
                    <button
                      onClick={handleLastPage}
                      disabled={currentPage === totalPages}
                    >
                      {localizationStrings?.lastPage}
                    </button>
                  </div>
                </div>
              </div>
              {/* ====== USER TAB END ====== */}
            </div>
            <div
              className="tab-pane fade"
              id="role-tab-pane"
              role="tabpanel"
              aria-labelledby="role-tab"
              tabIndex="0"
            >
              <div className="inc-card-table">
                <table className="full-table m-head inc-role-table">
                  <thead>
                    <tr>
                      <th>
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            checked={selectAllbox}
                            onChange={() => handleSelectAllbox()}
                          />{" "}
                          <span className="indicator"></span>
                        </label>
                      </th>
                      <th>{localizationStrings?.roleName}</th>
                      <th>{localizationStrings?.permissionName}</th>
                      {/* <th>{localizationStrings?.action}</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6">
                          <Loader />
                        </td>
                      </tr>
                    ) : (
                      allPermissions &&
                      allPermissions.map((role) => (
                        <tr key={role.role_id}>
                          <td>
                            <label className="checkbox">
                              <input
                                type="checkbox"
                                checked={
                                  individualboxChecked[role.role_name] || false
                                }
                                onChange={() =>
                                  handleIndividualboxChange(role.role_name)
                                }
                              />
                              <span className="indicator"></span>
                            </label>
                          </td>
                          <td>{role.role_name}</td>
                          <td>
                            {role.features.map((feature) => (
                              <div
                                key={feature.feature_id}
                                className="feature-container"
                              >
                                <strong>{feature.feature_name}:</strong>{" "}
                                {feature.permissions.join(", ")}
                              </div>
                            ))}
                          </td>
                          <td className="w200">
                            <div className="button-group">
                              <button title="Edit">
                                <i
                                  className="fa fa-pencil"
                                  aria-hidden="true"
                                ></i>
                              </button>
                              {/* <button
                                title="Delete"
                                data-bs-toggle="modal"
                                data-bs-target="#noDeviceDelete"
                                onClick={() => {
                                  handleDeleteAssignPermissionRole(
                                    role.role_id
                                  );
                                  fetchPermissionsForRoles();
                                }}
                              >
                                <i
                                  className="fa fa-trash"
                                  aria-hidden="true"
                                ></i>
                              </button> */}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div
              className="tab-pane fade"
              id="permission-tab-pane"
              role="tabpanel"
              aria-labelledby="permission-tab"
              tabIndex="0"
            >
              {/* Role Selection */}
              <div className="per-role-select">
                <select
                  className="form-select"
                  id="role"
                  name="role"
                  value={selectedRole}
                  onChange={(e) => handleRoleChange(e.target.value)}
                >
                  <option value="">{localizationStrings?.selectRole}</option>
                  {allPermission.map((role) => (
                    <option key={role.role_id} value={role.role_name}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Features and Permissions */}
              <div className="permission-wrapper">
                <div className="permission-name">
                  <ul id="nav-tab" role="tablist">
                    {selectedFeatures.map((feature, index) => (
                      <li
                        key={feature.feature_id}
                        id={`nav-${feature.feature_id}-tab`}
                        data-bs-toggle="tab"
                        data-bs-target={`#nav-${feature.feature_id}`}
                        type="button"
                        role="tab"
                        aria-controls={`nav-${feature.feature_id}`}
                        aria-selected={index === 0 ? "true" : "false"}
                        className={index === 0 ? "active" : ""}
                      >
                        {feature.feature_name}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="permission-type" id="nav-tabContent">
                  {selectedFeatures.map((feature, index) => (
                    <div
                      key={feature.feature_id}
                      className={`tab-pane fade ${
                        index === 0 ? "show active" : ""
                      }`}
                      id={`nav-${feature.feature_id}`}
                      role="tabpanel"
                      aria-labelledby={`nav-${feature.feature_id}-tab`}
                    >
                      <ul>
                        {feature.permissions.map((permission, idx) => (
                          <li key={`${feature.feature_id}-${idx}`}>
                            <label className="checkbox">
                              <input
                                type="checkbox"
                                checked={permission.isGranted}
                                onChange={() =>
                                  handlePermissionCheckboxChange(
                                    feature.feature_id,
                                    permission.permission_name
                                  )
                                }
                              />
                              <span className="indicator"></span>
                            </label>
                            {permission.permission_name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              <div className="permission-button mt-3">
                <button
                  className="text-btn primary-btn"
                  onClick={handleAssignPermission}
                  disabled={isSaveDisabled}
                >
                  {localizationStrings?.savePermission}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* <!-- ROLE Modal --> */}
        <div
          className="modal fade customModal"
          id="addRole"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-labelledby="addRoleLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="addUserLabel">
                  {localizationStrings?.newRole}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={handleCloseRoleInfo}
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="mb-3">
                    <label htmlFor="roleName" className="form-label">
                      {localizationStrings?.roleName}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="roleName"
                      value={roleName}
                      onChange={(e) => setRoleName(e.target.value)}
                    />
                  </div>
                  <div className="mb-3 d-flex justify-content-between">
                    <div className="radio-buttons">
                      {localizationStrings?.positionThisRole} {"  "}
                      <input
                        type="radio"
                        id="above"
                        name="rolePosition"
                        checked={aboveSelected}
                        onChange={() => setAboveSelected(true)}
                      />
                      <label htmlFor="above">
                        {localizationStrings?.above}
                      </label>
                      {localizationStrings?.or}{" "}
                      <input
                        type="radio"
                        id="below"
                        name="rolePosition"
                        checked={!aboveSelected}
                        onChange={() => setAboveSelected(false)}
                      />
                      <label htmlFor="below">
                        {localizationStrings?.below}
                      </label>
                      {localizationStrings?.theRole}
                    </div>
                    <a
                      href="#"
                      data-bs-dismiss="modal"
                      onClick={handleMoreInfoClick}
                    >
                      {localizationStrings?.moreInfo}
                    </a>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="roleAbove" className="form-label">
                      {localizationStrings?.roleList}
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      id="roleAbove"
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                    >
                      <option value="">
                        {localizationStrings?.selectRole}
                      </option>

                      {roles.map((role) => (
                        <option key={role.id} value={role.name}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={handleCloseRoleInfo}
                >
                  {localizationStrings?.cancel}
                </button>
                <button
                  type="button"
                  className="btn btn-fill"
                  onClick={handleAddRole}
                  data-bs-dismiss="modal"
                >
                  {localizationStrings?.create}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* <!-- Permission Modal --> */}
        <div
          className="modal fade customModal"
          id="addPermission"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-labelledby="addPermissionLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="addUserLabel">
                  {localizationStrings?.newPermission}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="row">
                    <div className="col-lg-12">
                      <label htmlFor="startIp" className="col-form-label">
                        {localizationStrings?.permissionName}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={permissionName}
                        onChange={(e) => setPermissionName(e.target.value)}
                      />
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  {localizationStrings?.cancel}
                </button>
                <button
                  type="button"
                  className="btn btn-fill"
                  onClick={handleAddPermission}
                >
                  {localizationStrings?.create}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showRoleInfo && (
        <RoleInfo
          showPopUp={showRoleInfo}
          handleClose={handleCloseRoleInfo}
          headerMessage1="Role Information"
        />
      )}
      <UserAddModal
        showPopUp={showUserPopUp}
        handleClose={handleUserClose}
        headerMessage1={localizationStrings?.newUser}
        onSuccess={handleSuccessAddRole}
      />
      <EditUserModal
        showPopUp={showEditModal}
        handleClose={() => setShowEditModal(false)}
        user={selectedUser}
        showSuccessPopUp={showSuccessPopUp}
      />
      <div
        className={`modal fade`}
        id="noDeviceDelete"
        tabIndex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content inc-modal">
            <div className="modal-body success-msg">
              <img src={errorIcon} alt="error" />
              <p>{localizationStrings?.deleteConfirmation}</p>
              <button
                className="text-btn primary-btn"
                data-bs-dismiss="modal"
                onClick={handleDeleteConfirmation}
              >
                {localizationStrings?.yes}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`modal fade`}
        id="roleDelete"
        tabIndex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content inc-modal">
            <div className="modal-body success-msg">
              <img src={errorIcon} alt="error" />
              <p>{localizationStrings?.deleteConfirmation}</p>
              <button
                className="text-btn primary-btn"
                data-bs-dismiss="modal"
                onClick={handleDeleteRoleConfirmation}
              >
                {localizationStrings?.yes}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={showSuccessModal}
        onHide={handleSuccessModalClose}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="inc-alert"
      >
        <span className="success-icon">
          <i className="icon done"></i>
        </span>
        <h3>{localizationStrings?.success}</h3>
        <p>{localizationStrings?.roleAddInfo}</p>
        <Button
          onClick={() => {
            handleSuccessModalClose();
          }}
        >
          {localizationStrings?.ok}
        </Button>
      </Modal>
      <Modal show={showSuccessModal} onHide={handleSuccessModalClose} centered>
        <Modal.Body className="edge-error-msg">
          <img src={successIcon} alt="Success" />
          <h2>{modalContent.title}</h2>
          <p>{modalContent.message}</p>
          <Button
            variant="primary"
            onClick={() => {
              handleSuccessModalClose();
              fetchUsers(networkId);
            }}
          >
            {localizationStrings?.ok}
          </Button>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default UserManagement;
